import * as React from 'react'
import { connect } from 'react-redux'
import { State } from 'store'
import { AddMyselfCard } from 'snippet/addMyselfCard'
import { WordPressCard } from 'snippet/wordPressCard'
import { SendDeveloperCard } from 'snippet/sendDeveloperCard'
import { NeedHelpCardCard } from 'snippet/needHelpCard'
import { History } from 'history'
import { SnippetService } from 'snippet/snippetService'
import { PageTopMessage, HeaderMessageContainer } from 'uiComponents/messages'
import { Container, MessageContent, MessageText, MessageLink, TopContainer, BottomContainer } from './snippetContainer'
import { delay } from 'utils'
import { Account } from 'auth/state'
import { StyledATag, PageTitle, Headline, PageHeading } from 'uiComponents/typography'
import { BaseRouteParams, withNavigation, WithNavigationProps } from 'hocs'
import { t, Trans } from 'translations'

interface AddSnippetProps extends WithNavigationProps<BaseRouteParams> {
    history: History
    email: string
    accounts: Account[]
    snippetService: SnippetService
}

interface AddSnippetState {
    message: string
    messageVisible: boolean
    messageType: 'warn' | 'error' | 'success'
}

class AddSnippetPage extends React.Component<AddSnippetProps, AddSnippetState> {
    constructor(props: AddSnippetProps) {
        super(props)
        this.state = {
            message: '',
            messageVisible: false,
            messageType: 'warn',
        }
    }

    async componentDidMount() {
        await this.detectSnippet()
    }

    async componentDidUpdate(prevProps: AddSnippetProps) {
        if (prevProps.match.params.accountSlug !== this.props.match.params.accountSlug) {
            await this.detectSnippet()
        }
    }

    onCheckAgain = async () => {
        await this.hideMessage()
        await this.detectSnippet()
    }

    hideMessage = async () => {
        if (this.state.messageType) {
            this.setState({ messageVisible: false })
            await delay(600)
        }
    }

    detectSnippet = async () => {
        try {
            await this.props.snippetService.detectSnippet(this.props.match.params.accountSlug)
            const pathnameElements = location.pathname.split('/')
            if (pathnameElements.indexOf('new_account') > -1) {
                this.props.history.push('/')
            } else if (pathnameElements.indexOf('engage/tools') > -1) {
                this.props.history.push(`/account/${this.props.match.params.accountSlug}/engage/tools/home/`)
            } else {
                const account =
                    this.props.accounts.find((x) => x.slug === this.props.match.params.accountSlug) || this.props.accounts[0]
                this.setState({
                    message: t('snippet.page.detected', { url: account.url }),
                    messageType: 'success',
                    messageVisible: true,
                })
            }
        } catch (e) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            if (e.message === 'script not found') {
                this.setState({
                    messageType: 'warn',
                    messageVisible: true,
                })
            } else {
                this.handleUnknownError()
            }
        }
    }

    handleUnknownError = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        this.setState({
            message: t('snippet.page.unknownError'),
            messageType: 'error',
            messageVisible: true,
        })
    }

    render() {
        const { accounts, email, snippetService, match } = this.props
        const { message, messageVisible, messageType } = this.state
        const account = accounts.find((x) => x.slug === match.params.accountSlug) || accounts[0]

        return (
            <Container id="snippet-page" style={{ maxWidth: '1200px' }}>
                <div style={{ width: '100%' }}>
                    <HeaderMessageContainer>
                        <PageTopMessage
                            kind={messageType}
                            showMessage={messageVisible}
                            onDismiss={() => this.setState({ messageVisible: false })}
                        >
                            {(messageType === 'error' || messageType === 'success') && message}
                            {messageType === 'warn' && (
                                <MessageContent>
                                    <MessageText>{t('snippet.page.notDetected', { name: account.name })}</MessageText>
                                    <MessageLink onClick={this.onCheckAgain}>{t('snippet.page.checkAgain')}</MessageLink>
                                </MessageContent>
                            )}
                        </PageTopMessage>
                    </HeaderMessageContainer>
                </div>
                <TopContainer key="top">
                    <PageTitle>{t('snippet.page.title')}</PageTitle>
                    <PageHeading fullWidth>
                        {t('snippet.page.intro', { name: account.name })}
                        <div>
                            <Trans
                                i18nKey="snippet.page.guide"
                                components={{
                                    guide: (
                                        <StyledATag
                                            target="_blank"
                                            href="https://support.convious.com/help/convious-script-installation"
                                        />
                                    ),
                                }}
                            />
                        </div>
                    </PageHeading>
                </TopContainer>
                <BottomContainer key="bottom">
                    <Headline size={4}>{t('snippet.page.howToInstall')}</Headline>
                    <AddMyselfCard account={account} detectSnippet={this.onCheckAgain} />
                    <WordPressCard detectSnippet={this.onCheckAgain} />
                    <SendDeveloperCard
                        email={email}
                        account={account}
                        snippetService={snippetService}
                        handleUnknownError={this.handleUnknownError}
                    />
                    <NeedHelpCardCard />
                </BottomContainer>
            </Container>
        )
    }
}

const mapStateToProps = (state: State) => {
    return {
        email: state.auth.user ? state.auth.user.username : '',
        accounts: state.auth.user ? state.auth.user.accounts : [],
    }
}

export default connect(mapStateToProps)(withNavigation(AddSnippetPage))
